import { useEffect, useMemo, useState } from 'react'

import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import { Box, IconButton, InputAdornment } from '@mui/material'
import { MuiTelInput } from 'mui-tel-input'
import { useFormContext } from 'react-hook-form'

import { ControlledTelephoneNumWithCountryCode } from 'components/common'
import {
  GetBookingQuery,
  GetUserBookingQuery,
  useGetCustomerContactsQuery,
} from 'generated/graphql'
import { TBookingForm } from 'types/form-types'

interface IProps {
  bookingDetails: GetBookingQuery['booking'] | GetUserBookingQuery['userBooking']
  disabled: boolean
}

const ContactTelephoneNumberForm = (props: IProps) => {
  const { bookingDetails, disabled } = props
  const [isTelephoneNumberEditable, setTelephoneNumberEditable] = useState(false)

  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<TBookingForm>()

  const [customerIdValue, contactIdValue] = watch(['customerId', 'contactId'])

  const { data: customerContactsData } = useGetCustomerContactsQuery({
    fetchPolicy: 'network-only',
    variables: {
      customerId: Number(customerIdValue),
      where: { isActive: { eq: true } },
    },
    skip: !customerIdValue || customerIdValue.trim() === '',
  })

  const contactTelephoneNumberValue = useMemo(
    () =>
      customerContactsData?.customerContacts?.find(
        (contact) => contact.id === String(contactIdValue) && contact.isActive,
      )?.telephoneNumber,
    [contactIdValue, customerContactsData?.customerContacts],
  )

  useEffect(() => {
    if (bookingDetails?.overrideContactTelephoneNumber) {
      setTelephoneNumberEditable(true)
    }
  }, [bookingDetails?.overrideContactTelephoneNumber])

  const handleEditTelephoneNumber = () => {
    setTelephoneNumberEditable(true)

    if (bookingDetails?.overrideContactTelephoneNumber) {
      setValue('overrideContactTelephoneNumber', bookingDetails?.overrideContactTelephoneNumber)
    } else if (contactTelephoneNumberValue) {
      setValue('overrideContactTelephoneNumber', contactTelephoneNumberValue)
    }
  }

  const handleCancelEditTelephoneNumber = () => {
    setTelephoneNumberEditable(false)
    setValue('overrideContactTelephoneNumber', '')
  }

  return (
    <>
      <Box hidden={isTelephoneNumberEditable}>
        <MuiTelInput
          label='Telephone Number'
          name='contactTelephoneNumber'
          value={contactTelephoneNumberValue}
          disabled={disabled}
          fullWidth
          defaultCountry='GB'
          forceCallingCode={true}
          focusOnSelectCountry={true}
          flagSize='medium'
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton
                  size='small'
                  disabled={!contactIdValue || disabled}
                  title='Override Contact Tel No'
                  onClick={handleEditTelephoneNumber}>
                  <EditIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box hidden={!isTelephoneNumberEditable}>
        <ControlledTelephoneNumWithCountryCode
          control={control}
          name='overrideContactTelephoneNumber'
          label='Telephone Number'
          disabled={disabled}
          readOnly={false}
          type='tel'
          hideEndAdornment={false}
          endAdornment={
            <IconButton
              size='small'
              title='Use Default Contact Tel No'
              onClick={handleCancelEditTelephoneNumber}>
              <CloseIcon />
            </IconButton>
          }
          error={!!errors.overrideContactTelephoneNumber}
          helperText={errors.overrideContactTelephoneNumber?.message}
        />
      </Box>
    </>
  )
}

export default ContactTelephoneNumberForm
